import React from 'react';
import { formatDateReport } from '@app/utils/formatDateReport';
import { Space, Dropdown, type TableColumnsType } from '@app/ui';

interface ScheduleDataType {
    key: React.Key;
    createdAt: string;
    nextExecutionAt: string;
    emailSubject: string;
    creatorUserName: string;
    emailRecipients: string[];
}

export const getScheduleColumns = ({
    onEdit,
    onDelete,
    currentUserEmail,
    isGumgumUser,
    t,
}: {
    onEdit: (row: any) => void;
    onDelete: (row: any) => void;
    currentUserEmail: string;
    isGumgumUser: boolean;
    t: (key: string) => string;
}) => {
    const columns: TableColumnsType<ScheduleDataType> = [
        {
            title: t('reports.tables.schedules.columns.dateCreated'),
            dataIndex: 'createdAt',
            render: (value: string) => formatDateReport(value),
            sorter: (a: any, b: any) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            width: 120,
            minWidth: 80,
        },
        {
            title: t('reports.tables.schedules.columns.nextSchedule'),
            dataIndex: 'nextExecutionAt',
            render: (value: string) => formatDateReport(value),
            sorter: (a: any, b: any) =>
                new Date(b.nextExecutionAt).getTime() -
                new Date(a.nextExecutionAt).getTime(),
            width: 120,
            minWidth: 80,
        },
        {
            title: t('reports.tables.schedules.columns.reportName'),
            dataIndex: 'emailSubject',
            ellipsis: true,
            width: 250,
            minWidth: 150,
        },
        {
            title: t('reports.tables.schedules.columns.createdBy'),
            dataIndex: 'creatorUserName',
            sorter: (a: any, b: any) =>
                a.creatorUserName?.localeCompare(b.creatorUserName),
            width: 150,
            minWidth: 100,
            ellipsis: true,
        },
        {
            title: '',
            dataIndex: 'actions',
            align: 'center',
            render: (_: any, record: any) => {
                const canEdit =
                    record.emailRecipients?.includes(currentUserEmail) ||
                    isGumgumUser;

                if (!canEdit) return null;

                const items = [
                    {
                        key: 'edit',
                        label: t('reports.tables.schedules.actions.edit'),
                        icon: <i className="fa-regular fa-pencil" />,
                        onClick: () => onEdit(record),
                    },
                    {
                        key: 'delete',
                        label: t('reports.tables.schedules.actions.delete'),
                        icon: <i className="fa-regular fa-trash" />,
                        onClick: () => onDelete(record),
                    },
                ];

                return (
                    <Space size="middle">
                        <Dropdown
                            trigger={['hover']}
                            menu={{
                                items: items.map(
                                    ({ key, label, icon, onClick }) => ({
                                        key,
                                        label: (
                                            <div
                                                onClick={onClick}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                }}
                                            >
                                                {icon}
                                                {label}
                                            </div>
                                        ),
                                    }),
                                ),
                            }}
                        >
                            <a>
                                <i
                                    className="fa-solid fa-ellipsis-vertical"
                                    style={{
                                        fontSize: 16,
                                        color: '#009FDF',
                                    }}
                                />
                            </a>
                        </Dropdown>
                    </Space>
                );
            },
            width: 50,
            minWidth: 50,
            fixed: 'right',
        },
    ];

    return columns;
};
